/*
 * INOX Upsell Popup
 * Works with Dawn style AJAX carts (cart-drawer / cart-notification).
 * Everything is read from the section's theme editor settings.
 * Nothing is hard coded, so you should not need to edit this file.
 */

(() => {
  'use strict';

  const POPUP_SELECTOR = '[data-inox-upsell]';
  const SHOWN_KEY = 'inox-upsell-shown-';
  const EXPIRY_KEY = 'inox-upsell-expiry-';
  const CART_ADD_PATTERN = /\/cart\/add(?:\.js)?(?:\?|$)/;

  let popup = null;
  let dialog = null;
  let acceptButton = null;
  let errorElement = null;
  let countdownElement = null;
  let timerElement = null;
  let previousActiveElement = null;
  let isAdding = false;
  let suppressDetection = false;
  let countdownInterval = null;
  let offerExpired = false;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const inEditor = () => Boolean(window.Shopify && window.Shopify.designMode);
  const ds = (key, fallback = '') => (popup && popup.dataset[key] !== undefined ? popup.dataset[key] : fallback);
  const flag = (key) => ds(key) === 'true';

  function findElements() {
    popup = document.querySelector(POPUP_SELECTOR);
    if (!popup) return false;
    dialog = popup.querySelector('.inox-upsell__dialog');
    acceptButton = popup.querySelector('[data-inox-accept]');
    errorElement = popup.querySelector('[data-inox-error]');
    countdownElement = popup.querySelector('[data-inox-countdown]');
    timerElement = popup.querySelector('[data-inox-timer]');
    if (acceptButton && !acceptButton.dataset.originalText) {
      acceptButton.dataset.originalText = acceptButton.textContent.trim();
    }
    return true;
  }

  /* ---------- settings ---------- */

  function triggerIds() {
    return ds('triggerProductIds')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter(Boolean);
  }
  const upsellVariantId = () => Number(ds('upsellVariantId', 0));
  const upsellProductId = () => Number(ds('upsellProductId', 0));
  const variantAvailable = () => flag('variantAvailable');
  const openDelay = () => Math.max(0, Number(ds('openDelay', 200)) || 0);
  const countdownEnabled = () => flag('enableCountdown');
  const countdownMinutes = () => {
    const m = Number(ds('countdownMinutes', 5));
    return Number.isFinite(m) && m > 0 ? m : 5;
  };
  const sectionKey = () => ds('sectionId', 'default');

  /* ---------- session frequency ---------- */

  function hasShownThisSession() {
    if (inEditor() || ds('showFrequency') === 'every_time') return false;
    try { return sessionStorage.getItem(SHOWN_KEY + sectionKey()) === 'true'; } catch (_) { return false; }
  }

  function markShown() {
    if (inEditor() || ds('showFrequency') === 'every_time') return;
    try { sessionStorage.setItem(SHOWN_KEY + sectionKey(), 'true'); } catch (_) {}
  }

  /* ---------- trigger logic ---------- */

  function addedProductIds(data) {
    if (Array.isArray(data && data.items)) return data.items.map((i) => Number(i && i.product_id));
    return [Number(data && data.product_id)];
  }

  async function upsellAlreadyInCart() {
    if (!flag('skipIfInCart')) return false;
    try {
      const res = await window.fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const pid = upsellProductId();
      const vid = upsellVariantId();
      return (cart.items || []).some((i) => Number(i.variant_id) === vid || (pid && Number(i.product_id) === pid));
    } catch (_) {
      return false;
    }
  }

  async function shouldOpen(data) {
    if (!popup && !findElements()) return false;
    if (hasShownThisSession()) return false;
    if (!upsellVariantId()) return false;

    const added = addedProductIds(data);

    // Never trigger off the upsell product itself.
    if (upsellProductId() && added.includes(upsellProductId())) return false;

    const triggers = triggerIds();
    if (triggers.length && !added.some((id) => triggers.includes(id))) return false;

    if (await upsellAlreadyInCart()) return false;
    return true;
  }

  async function handleCartAdd(data) {
    const ok = await shouldOpen(data);
    if (ok) window.setTimeout(openPopup, openDelay());
  }

  /* ---------- countdown ---------- */

  const pad = (n) => String(n).padStart(2, '0');
  const formatTime = (secs) => `${pad(Math.floor(Math.max(0, secs) / 60))}:${pad(Math.max(0, secs) % 60)}`;

  function getExpiry() {
    const fresh = Date.now() + countdownMinutes() * 60000;
    if (inEditor()) return fresh;
    try {
      let expiry = Number(sessionStorage.getItem(EXPIRY_KEY + sectionKey()));
      if (!expiry || expiry < Date.now()) {
        expiry = fresh;
        sessionStorage.setItem(EXPIRY_KEY + sectionKey(), String(expiry));
      }
      return expiry;
    } catch (_) {
      return fresh;
    }
  }

  function stopCountdown() {
    if (countdownInterval) window.clearInterval(countdownInterval);
    countdownInterval = null;
  }

  function resetAcceptButton() {
    offerExpired = false;
    if (!acceptButton) return;
    const available = variantAvailable();
    acceptButton.disabled = !available;
    if (available) acceptButton.removeAttribute('aria-disabled');
    acceptButton.textContent = acceptButton.dataset.originalText || acceptButton.textContent;
  }

  function expireOffer() {
    stopCountdown();
    offerExpired = true;
    if (timerElement) timerElement.textContent = '00:00';
    if (acceptButton) {
      acceptButton.disabled = true;
      acceptButton.setAttribute('aria-disabled', 'true');
      acceptButton.textContent = ds('expiredButtonText', 'Offer expired');
    }
    if (countdownElement) countdownElement.classList.add('is-expired');
  }

  function startCountdown() {
    stopCountdown();
    resetAcceptButton();
    if (!countdownEnabled() || !countdownElement || !timerElement) return;

    countdownElement.classList.remove('is-expired');
    const expiry = getExpiry();
    const tick = () => {
      const left = Math.max(0, Math.ceil((expiry - Date.now()) / 1000));
      timerElement.textContent = formatTime(left);
      if (left <= 0) {
        try { sessionStorage.removeItem(EXPIRY_KEY + sectionKey()); } catch (_) {}
        expireOffer();
      }
    };
    tick();
    countdownInterval = window.setInterval(tick, 1000);
  }

  /* ---------- open / close ---------- */

  function openPopup() {
    if (!findElements()) return;
    if (popup.classList.contains('is-active')) return;

    previousActiveElement = document.activeElement;
    clearError();
    markShown();
    startCountdown();

    popup.setAttribute('aria-hidden', 'false');
    popup.classList.add('is-active');
    document.documentElement.classList.add('inox-upsell-open');
    document.body.classList.add('inox-upsell-open');
    window.requestAnimationFrame(() => dialog && dialog.focus({ preventScroll: true }));
  }

  function closePopup() {
    if (!popup && !findElements()) return;
    stopCountdown();
    popup.classList.remove('is-active');
    popup.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('inox-upsell-open');
    document.body.classList.remove('inox-upsell-open');
    if (previousActiveElement instanceof HTMLElement) previousActiveElement.focus({ preventScroll: true });
  }

  function handleKeydown(event) {
    if (!popup || !popup.classList.contains('is-active')) return;

    if (event.key === 'Escape' && flag('closeOnEscape')) {
      closePopup();
      return;
    }
    if (event.key !== 'Tab' || !dialog) return;

    const focusable = Array.from(
      dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);

    if (!focusable.length) { event.preventDefault(); dialog.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------- add to cart ---------- */

  function showError(message) {
    if (!errorElement) return;
    errorElement.textContent = message || ds('errorText', 'Something went wrong. Please try again.');
    errorElement.hidden = false;
  }

  function clearError() {
    if (!errorElement) return;
    errorElement.textContent = '';
    errorElement.hidden = true;
  }

  function setLoading(loading) {
    if (!acceptButton || offerExpired) return;
    acceptButton.disabled = loading;
    if (loading) {
      acceptButton.setAttribute('aria-busy', 'true');
      acceptButton.textContent = ds('acceptLoadingText', 'Adding...');
    } else {
      acceptButton.removeAttribute('aria-busy');
      acceptButton.textContent = acceptButton.dataset.originalText || acceptButton.textContent;
    }
  }

  const cartComponent = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  async function addUpsell() {
    if (!popup && !findElements()) return;
    if (offerExpired || isAdding || !variantAvailable()) return;

    const variantId = upsellVariantId();
    if (!variantId) return;

    if (inEditor()) { closePopup(); return; } // don't add real items while editing

    isAdding = true;
    suppressDetection = true;
    clearError();
    setLoading(true);

    try {
      const cart = cartComponent();
      const body = new FormData();
      body.append('id', String(variantId));
      body.append('quantity', '1');

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
      }

      const response = await window.fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || ds('errorText'));
      }

      if (cart && typeof cart.renderContents === 'function' && data.sections) {
        cart.renderContents(data);
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      closePopup();

      if (flag('openCartAfterAdd')) {
        window.setTimeout(() => { if (cart && typeof cart.open === 'function') cart.open(); }, 150);
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : '');
    } finally {
      setLoading(false);
      isAdding = false;
      window.setTimeout(() => { suppressDetection = false; }, 0);
    }
  }

  /* ---------- detect cart adds (fetch + XHR) ---------- */

  function installFetchInterceptor() {
    if (window.__inoxFetchInterceptorInstalled) return;
    window.__inoxFetchInterceptorInstalled = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const req = args[0];
        const url = typeof req === 'string' ? req : req instanceof Request ? req.url : String(req || '');
        if (!suppressDetection && CART_ADD_PATTERN.test(url) && response.ok) {
          response.clone().json().then(handleCartAdd).catch(() => {});
        }
      } catch (_) {}
      return response;
    };
  }

  function installXHRInterceptor() {
    if (window.__inoxXHRInterceptorInstalled) return;
    window.__inoxXHRInterceptorInstalled = true;

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__inoxUrl = String(url || '');
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (!suppressDetection && CART_ADD_PATTERN.test(this.__inoxUrl || '')) {
        this.addEventListener('load', () => {
          try {
            if (this.status >= 200 && this.status < 300) handleCartAdd(JSON.parse(this.responseText));
          } catch (_) {}
        });
      }
      return originalSend.apply(this, args);
    };
  }

  /* ---------- init ---------- */

  function bind() {
    if (!findElements()) return;
    if (popup.dataset.inoxBound === 'true') return;
    popup.dataset.inoxBound = 'true';

    popup.querySelectorAll('[data-inox-close], [data-inox-decline]').forEach((btn) => btn.addEventListener('click', closePopup));
    if (acceptButton) acceptButton.addEventListener('click', addUpsell);
  }

  function initialise() {
    bind();
    installFetchInterceptor();
    installXHRInterceptor();
  }

  function isOurSection(event) {
    return Boolean(event && event.target && event.target.querySelector && event.target.querySelector(POPUP_SELECTOR));
  }

  document.addEventListener('keydown', handleKeydown);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  // Theme editor: re-bind after every settings change, and preview when selected.
  document.addEventListener('shopify:section:load', (event) => {
    if (!isOurSection(event)) return;
    popup = null;
    initialise();
    if (flag('previewInEditor')) openPopup();
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (!isOurSection(event)) return;
    findElements();
    if (flag('previewInEditor')) openPopup();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (isOurSection(event)) closePopup();
  });

  window.InoxUpsell = { open: openPopup, close: closePopup, initialise };
})();
